import {useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Box} from '@mui/material';

import {Loading} from '../../../../components/Loading/Loading';
import {ModulePageHeader} from '../../../../components/ModulePageHeader/ModulePageHeader';
import {CompanyContext} from '../../../../contexts/CompanyContext/CompanyContext';
import {WorkforceDashboardActionList} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardActionList';
import {WorkforceDashboardAlignmentDistribution} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardAlignmentDistribution';
import {WorkforceDashboardAlignmentShapeChart} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardAlignmentShapeChart';
import {WorkforceDashboardDepartmentHealth} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardDepartmentHealth';
import {WorkforceDashboardDimensionImbalances} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardDimensionImbalances';
import {WorkforceDashboardKpiStrip} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardKpiStrip';
import {WORKFORCE_DASHBOARD_OVERVIEW_GRID_SX} from '../../../../features/company/workforce/components/WorkforceDashboard/workforceDashboardLayoutConstants';
import {WorkforceDashboardRecurringSkillGaps} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardRecurringSkillGaps';
import {WorkforceDashboardRedeploymentCandidates} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardRedeploymentCandidates';
import {WorkforceDashboardRoleCoverageHeatmap} from '../../../../features/company/workforce/components/WorkforceDashboard/WorkforceDashboardRoleCoverageHeatmap';
import {useWorkforceDashboardMetrics} from '../../../../features/company/workforce/hooks/useWorkforceDashboardMetrics';
import {Walkthrough, WalkthroughTrigger} from '../../../../features/walkthroughs';
import {
  companyWorkforceDashboardWalkthrough,
  WORKFORCE_DASHBOARD_TOUR_TARGETS,
} from '../../../../features/walkthroughs/steps/workforceDashboard';
import {AppConfig} from '../../../../lib/AppConfig';

const dashboardTwoColumnGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
  alignItems: 'start',
  '& > *': {minWidth: 0},
} as const;

const dashboardPairedSectionGridSx = {
  ...dashboardTwoColumnGridSx,
  alignItems: 'stretch',
  '& > *': {minWidth: 0, height: '100%'},
} as const;

export const WorkforceDashboardPage = () => {
  const navigate = useNavigate();
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage'});
  const {company} = useContext(CompanyContext);
  const companyId = company.data?.resourceId ?? '';
  const metrics = useWorkforceDashboardMetrics(companyId, 0);

  const hasSectionError = useMemo(
    () =>
      !!metrics.overview.error ||
      !!metrics.alignmentDistribution.error ||
      !!metrics.alignmentShape.error ||
      !!metrics.departmentHealth.error ||
      !!metrics.recurringSkillGaps.error ||
      !!metrics.scoreDimensionImbalances.error ||
      !!metrics.redeploymentCandidates.error ||
      !!metrics.profilesRequiringAction.error ||
      !!metrics.roleCoverageHeatmap.error,
    [
      metrics.alignmentDistribution.error,
      metrics.alignmentShape.error,
      metrics.departmentHealth.error,
      metrics.overview.error,
      metrics.profilesRequiringAction.error,
      metrics.recurringSkillGaps.error,
      metrics.redeploymentCandidates.error,
      metrics.roleCoverageHeatmap.error,
      metrics.scoreDimensionImbalances.error,
    ]
  );

  const openProfile = (resourceId: string) => {
    navigate(`${AppConfig.COMPANY_WORKFORCE_ANALYSIS_PROFILES_PATH}/${resourceId}`);
  };

  const isDashboardContentReady = !(metrics.isLoading && !metrics.overview.data);

  return (
    <Box sx={{maxWidth: 1120, mx: 'auto', width: '100%'}}>
      <ModulePageHeader
        moduleName={t('breadcrumb')}
        title={t('title')}
        titleDataTour={WORKFORCE_DASHBOARD_TOUR_TARGETS.title}
        actions={<WalkthroughTrigger walkthroughId='companyWorkforceDashboard' />}
      />

      {metrics.isLoading && !metrics.overview.data ? (
        <Box sx={{py: 8}}>
          <Loading />
        </Box>
      ) : (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          <Box sx={WORKFORCE_DASHBOARD_OVERVIEW_GRID_SX}>
            <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.kpiStrip} sx={{display: 'contents'}}>
              <WorkforceDashboardKpiStrip data={metrics.overview.data} fillHeight />
            </Box>
            <Box
              data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.alignmentDistribution}
              sx={{height: '100%', minHeight: 0}}
            >
              <WorkforceDashboardAlignmentDistribution
                data={metrics.alignmentDistribution.data}
                fillHeight
              />
            </Box>
          </Box>

          <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.alignmentShape}>
            <WorkforceDashboardAlignmentShapeChart data={metrics.alignmentShape.data} />
          </Box>

          <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.departmentHealth}>
            <WorkforceDashboardDepartmentHealth data={metrics.departmentHealth.data} />
          </Box>

          <Box sx={dashboardPairedSectionGridSx}>
            <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.recurringSkillGaps}>
              <WorkforceDashboardRecurringSkillGaps data={metrics.recurringSkillGaps.data} fillHeight />
            </Box>
            <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.dimensionImbalances}>
              <WorkforceDashboardDimensionImbalances
                data={metrics.scoreDimensionImbalances.data}
                onProfileClick={openProfile}
                fillHeight
              />
            </Box>
          </Box>

          <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.roleCoverageHeatmap}>
            <WorkforceDashboardRoleCoverageHeatmap data={metrics.roleCoverageHeatmap.data} />
          </Box>

          <Box sx={dashboardTwoColumnGridSx}>
            <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.redeploymentCandidates}>
              <WorkforceDashboardRedeploymentCandidates
                data={metrics.redeploymentCandidates.data}
                onProfileClick={openProfile}
              />
            </Box>
            <Box data-tour={WORKFORCE_DASHBOARD_TOUR_TARGETS.profilesRequiringAction}>
              <WorkforceDashboardActionList data={metrics.profilesRequiringAction.data} onProfileClick={openProfile} />
            </Box>
          </Box>

          {hasSectionError && (
            <Box sx={{fontSize: 13, color: 'text.secondary'}}>
              {t('sectionError')}
            </Box>
          )}
        </Box>
      )}

      <Walkthrough definition={companyWorkforceDashboardWalkthrough} enabled={isDashboardContentReady} />
    </Box>
  );
};
